/**
 * Clé de regroupement des exercices (même vidéo + même œuvre + même mouvement)
 */

import { getComposerGivenName } from '../data/composerGivenNames'

function getGroupKey(exercise) {
  const videoId = exercise.video?.id ?? ''
  const workTitle = (exercise.metadata?.workTitle ?? '').trim()
  const movementTitle = (exercise.metadata?.movementTitle ?? '').trim()
  return `${videoId}\0${workTitle}\0${movementTitle}`
}

function formatTimeCompact(seconds) {
  if (seconds == null || seconds < 0) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Retourne le rang (1-based) de l'exercice dans son groupe, avec tri stable (createdAt puis id).
 * @param {Object} exercise
 * @param {Array} allExercises - liste contenant exercise (même auteur ou même contexte d'affichage)
 * @returns {{ rank: number, groupSize: number }} rank = 1, 2, 3... ; groupSize = nombre d'exercices dans le groupe
 */
export function getExerciseGroupRank(exercise, allExercises) {
  if (!allExercises || !Array.isArray(allExercises) || allExercises.length === 0) {
    return { rank: 1, groupSize: 1 }
  }
  const key = getGroupKey(exercise)
  const group = allExercises.filter((ex) => getGroupKey(ex) === key)
  if (group.length <= 1) {
    return { rank: 1, groupSize: group.length || 1 }
  }
  const sorted = [...group].sort((a, b) => {
    const tA = a.createdAt?.toDate?.()?.getTime() ?? a.createdAt ?? 0
    const tB = b.createdAt?.toDate?.()?.getTime() ?? b.createdAt ?? 0
    if (tA !== tB) return tA - tB
    return (a.id || '').localeCompare(b.id || '')
  })
  const index = sorted.findIndex((ex) => ex.id === exercise.id)
  const rank = index >= 0 ? index + 1 : 1
  return { rank, groupSize: sorted.length }
}

/**
 * Libellé affiché pour un exercice : "Œuvre – Mouvement" (sans suffixe de rang).
 * @param {Object} exercise
 * @param {Array} allExercises - conservé pour compatibilité d'API, non utilisé pour le titre
 * @returns {string}
 */
export function getExerciseDisplayTitle(exercise, allExercises = []) {
  const workTitle = (exercise.metadata?.workTitle || exercise.metadata?.exerciseTitle || exercise.metadata?.title || '').trim()
  const movementTitle = (exercise.metadata?.movementTitle ?? '').trim()
  const base = workTitle
    ? (movementTitle ? `${workTitle} – ${movementTitle}` : workTitle)
    : (exercise.metadata?.exerciseTitle || exercise.metadata?.title || 'Sans titre')

  return base
}

/**
 * Retourne le nom du compositeur pour l'affichage (Prénom Nom).
 * Utilise composerGivenName + composerFamilyName si présents, sinon metadata.composer.
 * @param {{ composerGivenName?: string, composerFamilyName?: string, composer?: string }} metadata
 * @returns {string}
 */
export function getComposerDisplayName(metadata) {
  if (!metadata) return ''
  const given = (metadata.composerGivenName ?? '').trim()
  const family = (metadata.composerFamilyName ?? '').trim()
  if (family || given) return [given, family].filter(Boolean).join(' ').trim()
  return (metadata.composer ?? '').trim()
}

/**
 * Retourne le nom de famille du compositeur pour le tri (ou la chaîne complète si pas encore migré).
 * @param {{ composerFamilyName?: string, composer?: string }} metadata
 * @returns {string}
 */
export function getComposerFamilyName(metadata) {
  if (!metadata) return ''
  const family = (metadata.composerFamilyName ?? '').trim()
  if (family) return family
  return (metadata.composer ?? '').trim()
}

/**
 * Retourne le compositeur au format court pour les listes (ex. "Bach J.S.") à partir du nom de famille seul.
 * Utilise la table COMPOSER_GIVEN_NAMES pour les initiales.
 * @param {string} familyName - Nom de famille (ex. "Bach", "Mozart")
 * @returns {string}
 */
export function getComposerDisplayNameShortFromFamilyName(familyName) {
  if (!familyName || typeof familyName !== 'string') return ''
  const family = familyName.trim()
  if (!family) return ''
  const given = getComposerGivenName(family)
  if (!given) return family
  const initials = given.split(/\s+/).filter(Boolean).map((part) => part.charAt(0).toUpperCase() + '.').join('')
  return `${family} ${initials}`.trim()
}

/**
 * Retourne le compositeur au format court pour les listes de filtres : "Nom X." (ex. "Bach J.S.", "Mozart W.A.").
 * Utilise composerFamilyName + composerGivenName si présents ; sinon parse metadata.composer.
 * Enrichit avec la table de prénoms si le parse ne donne pas de prénom (ex. "Dvořák" → "D. ").
 * @param {{ composerGivenName?: string, composerFamilyName?: string, composer?: string }} metadata
 * @returns {string}
 */
export function getComposerDisplayNameShort(metadata) {
  if (!metadata) return ''
  let family = (metadata.composerFamilyName ?? '').trim()
  let given = (metadata.composerGivenName ?? '').trim()
  if (!family && !given && metadata.composer) {
    const parsed = parseComposerToGivenAndFamily(metadata.composer)
    family = parsed.familyName
    given = parsed.givenName
    if (!given && family) given = getComposerGivenName(metadata.composer) || ''
  }
  const initials = given
    ? given.split(/\s+/).filter(Boolean).map((part) => part.charAt(0).toUpperCase() + '.').join('')
    : ''
  if (family) return initials ? `${family} ${initials}`.trim() : family
  return (metadata.composer ?? '').trim()
}

/**
 * Parse une chaîne "Prénom Nom" ou "Nom, Prénom" en { givenName, familyName } (pour chargement édition / migration).
 * @param {string} composerString
 * @returns {{ givenName: string, familyName: string }}
 */
export function parseComposerToGivenAndFamily(composerString) {
  const s = (composerString ?? '').trim()
  if (!s) return { givenName: '', familyName: '' }
  if (s.includes(',')) {
    const idx = s.indexOf(',')
    const familyName = s.slice(0, idx).trim()
    const givenName = s.slice(idx + 1).trim()
    return { familyName, givenName }
  }
  const lastSpace = s.lastIndexOf(' ')
  if (lastSpace <= 0) return { familyName: s, givenName: '' }
  return { familyName: s.slice(lastSpace + 1).trim(), givenName: s.slice(0, lastSpace).trim() }
}

/**
 * Sous-titre contextuel pour différencier les exercices d'un même groupe.
 * Priorité : metadata.subtitle > extrait temporel [MM:SS – MM:SS] > null.
 * Retourne null si l'exercice est seul dans son groupe.
 */
export function getExerciseSubtitle(exercise, allExercises = []) {
  const { groupSize } = getExerciseGroupRank(exercise, allExercises)

  if (exercise.metadata?.subtitle) return exercise.metadata.subtitle

  if (groupSize <= 1) return null

  const start = exercise.settings?.startTime
  const end = exercise.settings?.endTime
  if (start != null && end != null) {
    return `${formatTimeCompact(start)} – ${formatTimeCompact(end)}`
  }
  return null
}
